import {useCallback} from 'react';
import {useIntl} from 'react-intl';

import {ListBox, ListBoxItem} from '../../../atoms/listbox';

import type {Props} from './types';
import {LoadingState} from './loading-state';
import {EmptyState} from './empty-state';

// eslint-disable-next-line @typescript-eslint/naming-convention
enum DEFAULT_LIST_ITEM_KEYS {
    LOADING = 'loading',
    EMPTY = 'empty',
}

export const AutocompleteList = <ListItem, >({
    isLoading = false,
    items,
    query,
    getKeyFromItem,
    getLabelFromItem,
    renderItem,
    onSelect,
    ariaListBoxProps,
}: Props<ListItem>) => {
    const {formatMessage} = useIntl();
    let content;

    const handleListItemSelect = useCallback(
        ({currentKey}) => {
            if (Object.values(DEFAULT_LIST_ITEM_KEYS).includes(currentKey)) {
                return;
            }

            const foundListItem = items.find((item) => getKeyFromItem(item) === currentKey);

            if (foundListItem) {
                onSelect(foundListItem);
            }
        },
        [getKeyFromItem, items, onSelect],
    );

    if (isLoading) {
        content = [
            <ListBoxItem key={DEFAULT_LIST_ITEM_KEYS.LOADING} textValue={DEFAULT_LIST_ITEM_KEYS.LOADING}>
                <LoadingState />
            </ListBoxItem>,
        ];
    } else if (items.length === 0) {
        content = [
            <ListBoxItem key={DEFAULT_LIST_ITEM_KEYS.EMPTY} textValue={DEFAULT_LIST_ITEM_KEYS.EMPTY}>
                <EmptyState query={query} />
            </ListBoxItem>,
        ];
    } else {
        content = items.map((item) => (
            <ListBoxItem textValue={getLabelFromItem(item)} key={getKeyFromItem(item)}>
                {renderItem(item)}
            </ListBoxItem>
        ));
    }

    return (
        <ListBox
            selectionMode='single'
            onSelectionChange={handleListItemSelect}
            aria-label={formatMessage({
                id: 'autocomplete.list',
                defaultMessage: 'Autocomplete List',
            })}
            {...ariaListBoxProps}
        >
            {content}
        </ListBox>
    );
};
